<template>
  <div class="min-h-screen relative">
    <Header />

    <div
      class="min-h-screen container mx-auto max-w-[1200px] w-full mb-10 py-7 flex flex-col gap-10 px-4 md:px-6 lg:gap-16"
    >
      <div>
        <div class="flex flex-row items-center justify-between">
          <p
            class="text-[#0F172A] text-xl sm:text-2xl md:text-3xl font-semibold"
          >
            Complete Course
          </p>
        </div>
        <div
          v-if="loading"
          class="flex items-center justify-center"
          style="height: calc(100vh - 350px) !important"
        >
          <img src="@/assets/newd/loading.gif" alt="" />
        </div>
        <div v-else>
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6"
            v-if="complete_course.length > 0"
          >
            <div
              v-for="course in this.complete_course"
              :key="course.id"
              class="bg-white shadow-lg rounded-lg p-4 sm:p-6 h-auto"
            >
              <div class="relative flex justify-center items-center">
                <img
                  v-if="course.thumbnail"
                  class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                  :src="course.thumbnail"
                  alt="Course Thumbnail"
                />
                <img
                  v-else
                  class="object-cover h-[180px] sm:h-[200px] w-full rounded-lg"
                  src="@/assets/coding.svg"
                  alt="Placeholder"
                />
              </div>
              <div class="text-center flex flex-col gap-2 mt-2">
                <p
                  class="text-sm sm:text-base text-gray-600 font-semibold text-left truncate"
                >
                  {{ course.course_title }}
                </p>

                <div class="flex gap-2 items-center mt-2">
                  <div
                    class="relative w-full bg-gray-200 rounded-full h-3 sm:h-4"
                  >
                    <div
                      class="absolute left-0 top-0 h-full bg-[#0085DB] rounded-full transition-all duration-300"
                      :style="{ width: course.percentage }"
                    ></div>
                  </div>
                  <p class="text-sm text-black">{{ course.percentage }}</p>
                </div>
                <router-link
                  v-if="course.image"
                  :to="{
                    name: 'CertificatePage',
                    params: {
                      complete_course_slug: course.course_slug,
                      complete_profile_slug: course.profile_slug,
                    },
                  }"
                  target="_blank"
                  class="text-sm sm:text-md text-[#0F172A] font-medium"
                >
                  <p
                    class="mt-3 py-2 text-xs sm:text-sm rounded-lg border border-[#0085DB] text-[#0F172A] font-medium text-center cursor-pointer hover:bg-[#0085DB] hover:text-white"
                  >
                    Preview
                  </p>
                </router-link>
                <button
                  v-else
                  @click="generateCertificate(course)"
                  class="w-full mt-3 py-2 rounded-lg border font-medium text-center transition-all duration-300"
                  :disabled="certificateLoading === course.id"
                  :class="
                    certificateLoading === course.id
                      ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-white border-[#0085DB] text-[#0085DB] hover:bg-[#0085DB] hover:text-white shadow-sm hover:shadow-md'
                  "
                >
                  <span
                    v-if="certificateLoading === course.id"
                    class="flex items-center justify-center gap-2"
                  >
                    <svg
                      class="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Loading...
                  </span>

                  <span v-else> Preview </span>
                </button>
              </div>
            </div>
          </div>
          <div
            v-else
            class="flex flex-col justify-center"
            style="height: calc(100vh - 350px) !important"
          >
            <p class="flex justify-center text-black">No Data Found</p>
          </div>
        </div>
      </div>
    </div>

    <!-- certificate image -->
    <div
      v-for="course in complete_course"
      :key="course.id"
      :id="'certificate-' + course.id"
      :ref="'certificate_' + course.id"
      class="fixed top-0 -left-[10000px] flex max-w-6xl h-[780px] bg-[#faf9f7] overflow-hidden"
    >
      <!-- Left White Section -->
      <div
        class="w-[60%] p-10 relative flex flex-col items-center justify-between bg-no-repeat bg-cover bg-center bg-[#faf9f7]"
        :style="{
          backgroundImage: `url(${require('@/assets/certificate/Layer_13.png')})`,
          backgroundSize: '60%',
          backgroundPosition: 'center center',
        }"
      >
        <div class="text-center mb-8">
          <h1
            class="text-6xl font-extrabold tracking-wider text-blue-900 uppercase mb-2"
            style="
              font-family: 'Cinzel', 'Times New Roman', serif;
              text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.05);
            "
          >
            CERTIFICATE
          </h1>

          <p
            class="text-2xl font-medium italic text-gray-600 tracking-wide"
            style="font-family: 'Playfair Display', Georgia, serif"
          >
            ______ of Completion ______
          </p>
        </div>

        <div
          class="text-2xl text-gray-700 mb-6 text-center"
          style="font-family: 'Cinzel', 'Times New Roman', serif"
        >
          This is to certify that
        </div>

        <div class="w-full flex-grow flex flex-col items-center justify-center">
          <div class="relative w-full max-w-xs mb-4">
            <div class="text-2xl font-semibold text-center text-gray-900">
              {{ course.display_name }}
            </div>
          </div>

          <div class="h-1 mb-2 w-full border-t-2 border-gray-800"></div>

          <div class="flex flex-col gap-4 text-center">
            <p
              class="text-xl font-medium italic text-gray-600 tracking-wide"
              style="font-family: 'Inter', sans-serif"
            >
              has successfully completed the course
            </p>

            <p
              class="text-xl font-bold text-black italic tracking-wide mb-5"
              style="font-family: 'Inter', sans-serif"
            >
              {{ course.course_title }}
            </p>
          </div>
        </div>

        <div class="w-full text-left mt-4 flex justify-between items-center">
          <div class="flex flex-col gap-1">
            <img
              v-if="qrCodeData"
              :src="qrCodeData"
              class="w-20 h-20 object-contain"
            />

            <p class="text-sm text-gray-600">
              S.I.No. {{ course.course_id || "000001" }}
            </p>
          </div>

          <div class="flex justify-center mt-10">
            <div class="text-center w-48">
              <div class="text-center">
                <img
                  src="@/assets/certificate/Rectangle(1).png"
                  alt=""
                  srcset=""
                />
              </div>
              <div class="border-t border-gray-400 w-full"></div>

              <p class="text-sm text-gray-600 mt-2">Authorized Signature</p>

              <p class="text-sm text-gray-600">Alliance Online</p>
            </div>
          </div>
        </div>
      </div>

      <div class="w-[45%] relative border-none">
        <svg
          class="absolute top-0 right-0 w-full h-full"
          viewBox="0 0 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_d_2412_4105)">
            <path
              d="M36.2714 726.854C30.5573 755.904 29.9879 786.296 34.9628 815.527C37.5002 830.407 41.4661 844.994 47.0603 858.735C48.0693 861.217 49.1882 863.732 50.5368 866H29.5783C28.2197 863.721 27.1008 861.194 26.0819 858.701C20.4876 844.949 16.5217 830.362 13.9843 815.493C9.00945 786.263 9.57886 755.87 15.293 726.82C21.097 697.286 32.0458 669.454 41.1864 641.047C61.6853 577.363 73.2734 509.235 69.2176 441.534C64.6323 365.023 40.8567 292.563 23.4046 219.154C5.68286 144.629 2.38627 76.4207 9.49896 0H30.4774C23.3647 76.432 26.6613 144.629 44.3831 219.165C61.8351 292.574 85.6108 365.046 90.1961 441.557C94.2519 509.257 82.6637 577.375 62.1648 641.081C53.0242 669.488 42.0755 697.319 36.2714 726.854Z"
              fill="#7C0902"
            />
            <path
              d="M500 0V900H56.641C55.2794 863.721 54.148 861.183 53.1268 858.69C47.52 844.95 43.5452 830.364 41.0022 815.485C36.0162 786.256 36.5869 755.866 42.3138 726.818C48.1308 697.285 59.104 669.456 68.265 641.051C88.8097 577.36 100.424 509.235 96.3589 441.54C91.7634 365.034 67.9346 292.566 50.4435 219.162C32.6822 144.63 29.3782 76.427 36.5068 0.0112807H446V0Z"
              fill="#003467"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_2412_4105"
              x="0"
              y="0"
              width="500"
              height="900"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dx="-6" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_2412_4105"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_2412_4105"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
        <div
          class="relative z-10 h-full p-10 text-white flex flex-col items-center"
        >
          <div class="mb-8 ml-10">
            <div class="w-[300px] h-[200px]">
              <img :src="logo_image" alt="" srcset="" class="object-contain" />
            </div>
          </div>

          <div
            v-if="course.profile_image"
            class="mb-8 flex justify-center md:justify-start"
          >
            <img
              :src="course.profile_image"
              crossorigin="anonymous"
              class="w-32 h-32 rounded-full object-cover shadow-lg"
            />
          </div>

          <div class="mt-auto flex flex-col gap-10 ml-20">
            <div class="w-full">
              <h3 class="text-lg font-bold mb-3 pb-2 inline-block">
                Course Coverage
              </h3>
              <ul class="space-y-3 text-xs">
                <li class="flex items-center">
                  <span class="mr-2"
                    ><img src="@/assets/Group(3).svg" alt="" srcset=""
                  /></span>
                  <span>Principles of corporate communication</span>
                </li>
                <li class="flex items-center">
                  <span class="mr-2"
                    ><img src="@/assets/Group(3).svg" alt="" srcset=""
                  /></span>
                  <span>Organizational and leadership communication</span>
                </li>
                <li class="flex items-center">
                  <span class="mr-2"
                    ><img src="@/assets/Group(3).svg" alt="" srcset=""
                  /></span>
                  <span>Professional written, verbal, and visual skills</span>
                </li>
                <li class="flex items-center">
                  <span class="mr-2"
                    ><img src="@/assets/Group(3).svg" alt="" srcset=""
                  /></span>
                  <span>Public relations and crisis communication</span>
                </li>
              </ul>
            </div>

            <div class="w-full text-center">
              <p class="text-xl font-light text-[#ED9740]">
                allianceonline.edu.in
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import axios from "axios";
import Footer from "@/components/Footer.vue";
import Header from "@/components/Student/Header.vue";
import { mapGetters } from "vuex";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
export default {
  name: "CompleteCourse",
  data() {
    return {
      complete_course: [],
      loading: true,
      logo_image: require(`${process.env.VUE_APP_CERTIFICATE_LOGO}`),
      qrCodeData: "",
      certificateLoading: false,
    };
  },

  components: {
    Header,
    Footer,
  },

  computed: {
    ...mapGetters(["userInfo"]),
  },
  created() {
    this.completecourse();
  },
  methods: {
    async completecourse() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/completed-certificates/?Key=${this.userInfo.token_key}&lesson_track__lesson_id__course_name__certificate_enable=true&lesson_track__user_id__id=${this.userInfo.user.id}&certificate_provide=true`,
        )
        .then((response) => {
          this.complete_course = response.data.data;
          setTimeout(() => {
            this.loading = false;
          }, 600);
          this.complete_course.forEach((element) => {
            element["end_time"] = new Date(element.end_time).toLocaleDateString(
              "en-IN",
              {
                day: "numeric",
                month: "long",
                year: "numeric",
              },
            );
          });
        });
      for (const course of this.complete_course) {
        if (course.course_slug && course.profile_slug) {
          const qrUrl =
            `${process.env.VUE_APP_FRONTEND_DOMAIN}/certificate/` +
            `?course_slug=${course.course_slug}` +
            `&profile_slug=${course.profile_slug}`;

          this.qrCodeData = await QRCode.toDataURL(qrUrl, {
            errorCorrectionLevel: "M",
            margin: 4,
            width: 256,
            color: {
              dark: "#000000",
              light: "#ffffff",
            },
          });
        }
      }
    },
    async generateCertificate(course) {
      try {
        // this.loading = true;
        this.certificateLoading = course.id;

        const certificateElement = Array.isArray(
          this.$refs[`certificate_${course.id}`],
        )
          ? this.$refs[`certificate_${course.id}`][0]
          : this.$refs[`certificate_${course.id}`];

        console.log("certificateElement", certificateElement);

        if (!certificateElement) {
          alert("Certificate element not found");
          return;
        }

        await this.$nextTick();

        const canvas = await html2canvas(certificateElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
        });

        console.log("canvas", canvas);

        const blob = await new Promise((resolve) => {
          canvas.toBlob((file) => {
            console.log("blob", file);
            resolve(file);
          }, "image/png");
        });

        if (!blob) {
          alert("Image generation failed");
          return;
        }

        const formData = new FormData();

        formData.append("image", blob, `certificate_${course.id}.png`);

        for (let pair of formData.entries()) {
          console.log(pair[0], pair[1]);
        }

        const response = await axios.put(
          `${process.env.VUE_APP_API}/student-certificate/update/${course.id}/?Key=${this.userInfo.token_key}`,
          formData,
        );

        console.log("response", response.data);
        //  this.$router.push({
        //           name: "CertificatePage",
        //           params: {
        //             complete_course_slug: course.course_slug,
        //             complete_profile_slug: course.profile_slug,
        //           },
        //         });
        const routeData = this.$router.resolve({
          name: "CertificatePage",
          params: {
            complete_course_slug: course.course_slug,
            complete_profile_slug: course.profile_slug,
          },
        });

        window.open(routeData.href, "_blank");
      } catch (error) {
        console.error(error);

        if (error.response) {
          console.log("Status:", error.response.status);
          console.log("Data:", error.response.data);
        }
      } finally {
        this.loading = false;
        this.certificateLoading = false;
      }
    },
  },
};
</script>

<style></style>
